import React from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatRupiah } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import LikeButton from "@/components/LikeButton";
import { Minus, Plus, ArrowLeft, Flame, Send, Trash2, Zap, ShieldCheck, Clock, Headphones } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [loadingComment, setLoadingComment] = useState(false);
  const { add } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false));
    loadComments();
  }, [id]);

  const loadComments = () => {
    api.get(`/products/${id}/comments`).then((r) => setComments(r.data)).catch(() => {});
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    if (!user) { toast.error("Login dulu untuk berkomentar"); return; }
    setLoadingComment(true);
    try {
      await api.post(`/products/${id}/comments`, { content: comment });
      setComment("");
      loadComments();
      toast.success("Komentar ditambahkan!");
    } catch (err) {
      toast.error("Gagal menambahkan komentar");
    } finally {
      setLoadingComment(false);
    }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Hapus komentar ini?")) return;
    try {
      await api.delete(`/products/${id}/comments/${commentId}`);
      loadComments();
      toast.success("Komentar dihapus");
    } catch (err) {
      toast.error("Gagal menghapus komentar");
    }
  };

  if (product === null) return <div className="p-12 font-mono">// LOADING</div>;
  if (product === false) return <div className="p-12 font-mono">// PRODUCT NOT FOUND</div>;

  const handleAdd = () => { add(product, qty); toast.success(`${product.name} ditambahkan ke keranjang`); };
  const handleBuyNow = () => { add(product, qty); nav("/cart"); };

  const isDigital = product.type === "digital";

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10" data-testid="product-detail">
      <Link to="/catalog" className="label-mono text-zinc-500 hover:text-black flex items-center gap-2">
        <ArrowLeft className="w-3 h-3" /> Kembali
      </Link>

      <div className="mt-6 grid lg:grid-cols-2 gap-0 border border-black">
        <div className="aspect-square overflow-hidden bg-zinc-100 border-r border-black">
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        </div>
        <div className="p-6 lg:p-10 flex flex-col">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="badge-status bg-[#FFD600]">{isDigital ? "DIGITAL" : "FISIK"}</span>
            {product.sku && <span className="badge-status bg-white">SKU: {product.sku}</span>}
            {product.sold_count > 0 && (
              <span className="badge-status bg-black text-white inline-flex items-center gap-1">
                <Flame className="w-3 h-3" /> {product.sold_count} TERJUAL
              </span>
            )}
            <LikeButton product={product} size="lg" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight mt-4">{product.name}</h1>
          <div className="font-mono text-3xl font-bold mt-4">{formatRupiah(product.price)}</div>
          <p className="text-sm text-zinc-700 mt-6 leading-relaxed">{product.description}</p>

          {!isDigital && (
            <div className="label-mono mt-4">// STOK: {product.stock}</div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-black">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-black hover:text-white"><Minus className="w-3 h-3" /></button>
              <input value={qty} onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} className="w-12 text-center font-mono font-bold py-3 border-x border-black focus:outline-none" />
              <button onClick={() => setQty(qty + 1)} className="p-3 hover:bg-black hover:text-white"><Plus className="w-3 h-3" /></button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={handleAdd} className="btn-secondary">+ Keranjang</button>
            <button onClick={handleBuyNow} className="btn-primary">Beli sekarang →</button>
          </div>

          {/* INFO FULFILLMENT & SUPPORT */}
          <div className="mt-auto pt-8 border-t border-zinc-200 grid grid-cols-2 gap-4">
            <div className="flex items-start gap-2">
              {isDigital ? (
                <Zap className="w-4 h-4 text-[#FF6A00] mt-0.5 flex-shrink-0" />
              ) : (
                <ShieldCheck className="w-4 h-4 text-[#00C853] mt-0.5 flex-shrink-0" />
              )}
              <div>
                <div className="font-mono text-xs font-bold">
                  {isDigital ? "Instan & Otomatis" : "Pengiriman Terjamin"}
                </div>
                <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                  {isDigital
                    ? "Dikirim dalam hitungan menit setelah pembayaran"
                    : "Dikemas aman, sampai dalam 2-4 hari kerja"}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Headphones className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-mono text-xs font-bold">Dukungan 24/7</div>
                <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                  Tim kami siap membantu kapan saja via live chat
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-mono text-xs font-bold">Transaksi Aman</div>
                <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                  Pembayaran diproses dengan sistem enkripsi terpercaya
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-mono text-xs font-bold">Garansi Kepuasan</div>
                <div className="font-mono text-[11px] text-zinc-500 mt-0.5">
                  Produk tidak sesuai? Kami kembalikan uangmu
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KOMENTAR */}
      <div className="mt-10 border border-black bg-white">
        <div className="p-4 border-b border-black flex items-center justify-between">
          <div>
            <div className="label-mono text-zinc-500">// ULASAN & KOMENTAR</div>
            <h2 className="font-display text-xl font-black uppercase mt-1">Komentar ({comments.length})</h2>
          </div>
        </div>

        <div className="p-6">
          {user ? (
            <form onSubmit={submitComment} className="flex gap-3 mb-6">
              <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-xs font-bold flex-shrink-0">
                {user.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 flex gap-2">
                <input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="input-brutal flex-1"
                  placeholder="Tulis komentar atau ulasan..."
                  maxLength={500}
                />
                <button type="submit" disabled={loadingComment || !comment.trim()} className="btn-primary px-4 flex items-center gap-2">
                  <Send className="w-3 h-3" />
                  {loadingComment ? "..." : "Kirim"}
                </button>
              </div>
            </form>
          ) : (
            <div className="mb-6 p-4 border border-black bg-zinc-50 font-mono text-xs text-center">
              <Link to="/login" className="font-bold underline">Login</Link> untuk menulis komentar
            </div>
          )}

          {comments.length === 0 ? (
            <div className="py-8 text-center font-mono text-xs text-zinc-500">// Belum ada komentar · jadilah yang pertama!</div>
          ) : (
            <div className="space-y-4">
              {comments.map((c) => (
                <div key={c.id} className="flex gap-3 p-4 border border-zinc-200">
                  <div className="w-8 h-8 bg-black text-white flex items-center justify-center font-mono text-xs font-bold flex-shrink-0">
                    {c.user_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="font-mono text-xs font-bold">{c.user_name}</div>
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] font-mono text-zinc-400">
                          {new Date(c.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
                        </div>
                        {(user?.id === c.user_id || user?.role === "admin") && (
                          <button onClick={() => deleteComment(c.id)} className="p-1 hover:text-red-500">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-zinc-700 mt-1">{c.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}